import { useLocaleStore } from '@/stores/shared/locale.pinia.ts';
import i18next from 'i18next';
import { storeToRefs } from 'pinia';

interface ITimeComposable {
  useDateTime: (
    inputDate: string | Date | undefined | null,
    options?: Intl.DateTimeFormatOptions,
    alwaysEnglish?: boolean
  ) => string;
  useTimeFormat: (
    inputDate: string | Date | undefined | null,
    options?: Intl.DateTimeFormatOptions
  ) => string | null;
  useTimeFromNow: (inputDate: string | Date | undefined | null) => string;
  useInputDateFormat: (
    inputDate: string | null | undefined | Date
  ) => string | null;
  usetimeToMinutesHoursSeconds: (time: number) => string;
  useInputOnlyDateFormat: (
    inputDate: string | null | undefined | Date
  ) => string | null;
  useGetDurationInDays: ({
    startDate,
    endDate,
  }: {
    startDate?: Date | null;
    endDate?: Date | null;
  }) => string;
}

/*
 * -----------------------------------------------------------------------
 * Composable
 * -----------------------------------------------------------------------
 * This composable is used to get the time of the application
 * -----------------------------------------------------------------------
 * @returns {ITimeComposable}
 * -----------------------------------------------------------------------
 * @example
 * import { useTimeComposable } from '@/composables/useTime.composable';
 * const { useDateTime, useTimeFormat, useTimeFromNow } = useTimeComposable();
 * -----------------------------------------------------------------------
 */
const useTimeComposable = (): ITimeComposable => {
  const { t } = i18next;

  /*
   * -----------------------------------------------------------------------
   * Methods
   * -----------------------------------------------------------------------
   * Description: Returns a formatted date string based on the input date
   * @param inputDate: string | Date | undefined|null
   * @param options: Intl.DateTimeFormatOptions
   * @returns string
   * -----------------------------------------------------------------------
   */
  const useDateTime = (
    inputDate: string | Date | undefined | null,
    options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: undefined,
      timeZoneName: undefined,
    },
    alwaysEnglish: boolean = false
  ): string => {
    const { locale } = storeToRefs(useLocaleStore());
    if (!inputDate) {
      return '';
    }
    const date = new Date(inputDate);
    return date.toLocaleDateString(
      !alwaysEnglish && locale.value === 'ar' ? 'ar' : undefined,
      options
    );
  };

  /*
   * -----------------------------------------------------------------------
   * Methods
   * -----------------------------------------------------------------------
   * Description: Returns a formatted time string based on the input date
   * @param inputDate: string | Date | undefined|null
   * @param options: Intl.DateTimeFormatOptions
   * @returns string
   * -----------------------------------------------------------------------
   * @example
   * useTimeFormat(new Date(), { hour: 'numeric', minute: 'numeric', hour12: true }) // 2:30 PM
   * -----------------------------------------------------------------------
   * @example
   * useTimeFormat(new Date(), { hour: 'numeric', minute: 'numeric', hour12: false }) // 14:30
   */
  const useTimeFormat = (
    inputDate: string | Date | undefined | null,
    options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }
  ): string | null => {
    const { locale } = storeToRefs(useLocaleStore());
    if (!inputDate) {
      return null;
    }
    const date = new Date(inputDate);
    return date.toLocaleTimeString(
      locale.value === 'ar' ? 'ar' : undefined,
      options
    );
  };

  const useInputDateFormat = (inputDate: string | null | undefined | Date) => {
    if (inputDate) {
      const date = new Date(inputDate);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else return null;
  };
  const useInputOnlyDateFormat = (
    inputDate: string | null | undefined | Date
  ) => {
    if (inputDate) {
      const dateObject = new Date(inputDate);
      const year = dateObject.getUTCFullYear();
      const month = String(dateObject.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateObject.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else {
      return null;
    }
  };
  /*
   * -----------------------------------------------------------------------
   * Methods
   * -----------------------------------------------------------------------
   * Description: Returns a formatted time string based on the input date
   * @param inputDate: string | Date | undefined|null
   * @returns string
   * -----------------------------------------------------------------------
   * @example
   * useTimeFromNow(new Date()) // just now
   * -----------------------------------------------------------------------
   * @example
   * useTimeFromNow(new Date().setMinutes(new Date().getMinutes() - 5)) // 5 minutes ago
   * -----------------------------------------------------------------------
   * @example
   * useTimeFromNow(new Date().setHours(new Date().getHours() - 1)) // 1 hour ago
   */
  const useTimeFromNow = (
    inputDate: string | Date | undefined | null
  ): string => {
    if (!inputDate) {
      return '';
    }
    // calculate the difference between now and the input date in seconds and round it
    const diff = Math.round(
      (new Date().getTime() - new Date(inputDate).getTime()) / 1000
    );
    // if the difference is less than a minute
    if (diff < 60) {
      return t('lms.justNow');
    }
    // if the difference is less than an hour
    if (diff < 3600) {
      const minutes = Math.round(diff / 60);
      return t('lms.minutesAgo', { minutes, count: minutes });
    }

    // if the difference is less than a day
    if (diff < 86400) {
      const hours = Math.round(diff / 3600);
      return t('lms.hoursAgo', { hours, count: hours });
    }

    // if the difference is less than a month
    if (diff < 2592000) {
      const days = Math.round(diff / 86400);
      return t('lms.daysAgo', { days, count: days });
    }

    // return the date in a readable format
    return useDateTime(inputDate);
  };

  /*
   * -----------------------------------------------------------------------
   * Method
   * -----------------------------------------------------------------------
   * Description: Converts a time value in seconds to a formatted string
   *              representing hours, minutes, and seconds.
   * @param time: number - The time value in seconds to be converted.
   * @returns string - The formatted time string in the 'HH:mm:ss' format.
   * -----------------------------------------------------------------------
   * @example
   * const totalTimeInSeconds = 3665; // 1 hour, 1 minute, and 5 seconds
   * const formattedTime = useTimeComposable().timeToMinutesHoursSeconds(totalTimeInSeconds);
   * // formattedTime will be '01:01:05'
   * -----------------------------------------------------------------------
   */

  const usetimeToMinutesHoursSeconds = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time - hours * 3600) / 60);
    const seconds = time - hours * 3600 - minutes * 60;

    // if zero add 0
    const hoursStr = hours < 10 ? '0' + hours : hours;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const secondsStr = seconds < 10 ? '0' + Math.round(seconds) : seconds;
    return hoursStr + ':' + minutesStr + ':' + secondsStr;
  };

  const useGetDurationInDays = ({
    startDate,
    endDate,
  }: {
    startDate?: Date | null;
    endDate?: Date | null;
  }) => {
    // Truncate time by converting to midnight UTC
    return startDate && endDate
      ? (
          (new Date(endDate).setUTCHours(0, 0, 0, 0) -
            new Date(startDate).setUTCHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24) +
          1
        ).toString()
      : '-';
  };

  return {
    useDateTime,
    useTimeFormat,
    useTimeFromNow,
    useInputOnlyDateFormat,
    useInputDateFormat,
    usetimeToMinutesHoursSeconds,
    useGetDurationInDays,
  };
};

export default useTimeComposable;
